import { memo, useRef, useState } from 'react';
import cn from 'classnames';
import { type TestProps } from '@direct-frontend/components/testing';
import { useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';
import styles from './ResizerHorizontal.module.css';

export interface ResizerHorizontalProps extends TestProps {
    className?: string;
    value: number;
    minWidth?: number;
    maxWidth?: number;
    onChange?: (newValue: number) => void;
    onChangeEnd: (newValue: number) => void;
    onDragging?: (isDragging: boolean) => void;
    reversed?: boolean;
}

export const ResizerHorizontal = memo<ResizerHorizontalProps>(function ResizerHorizontal({
    'data-testid': dataTestId = 'ResizerHorizontal',
    className,
    value,
    maxWidth = 1000,
    minWidth = 100,
    onChange,
    onChangeEnd,
    onDragging,
    reversed = false,
}) {
    if (minWidth > maxWidth) {
        throw new Error('minWidth must be less than maxWidth');
    }

    const ref = useRef<HTMLDivElement>(null);
    const refValue = useRef(value);

    refValue.current = value;

    const minDelta = value > minWidth ? minWidth - value : 0;
    const maxDelta = value < maxWidth ? maxWidth - value : 0;

    const configRef = useRef({ startX: 0, minDelta, maxDelta });

    configRef.current.minDelta = minDelta;

    configRef.current.maxDelta = maxDelta;

    const [isDragging, setIsDragging] = useState(false);

    useIsomorphicLayoutEffect(() => {
        const resizer = ref.current;

        if (!resizer) {
            return;
        }

        function calculateDelta(event: MouseEvent) {
            const { startX, maxDelta, minDelta } = configRef.current;

            const delta = (event.clientX - startX) * (reversed ? -1 : 1);

            if (delta < minDelta) {
                return minDelta;
            } else if (delta > maxDelta) {
                return maxDelta;
            }

            return delta;
        }

        function handleMouseMove(event: MouseEvent) {
            if (onChange) {
                onChange(refValue.current + calculateDelta(event));
                configRef.current.startX = event.clientX;
            } else if (resizer) {
                resizer.style.transform = `translate(${(reversed ? -1 : 1) * calculateDelta(event)}px, 0)`;
            }
        }

        function handleMouseUp(event: MouseEvent) {
            const delta = calculateDelta(event);

            if (resizer) {
                resizer.style.transform = ``;
            }

            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);

            onDragging?.(false);
            setIsDragging(false);
            onChangeEnd(Math.ceil(refValue.current + delta));
        }

        function handleMouseDown(event: MouseEvent) {
            configRef.current.startX = event.clientX;

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            onDragging?.(true);
            setIsDragging(true);
        }

        resizer.addEventListener('mousedown', handleMouseDown);

        return () => {
            resizer.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    return (
        <div
            data-testid={dataTestId}
            className={cn(styles.control, className, { [styles.dragging]: isDragging })}
            ref={ref}
        />
    );
});
